import {
	// ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	ViewChildren,
} from '@angular/core';
import { MATERIAL_MESH, MINIMUM_MATERIAL_OPACITY, SPLINE_MESH, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { EngineService } from '@core/services/engine/engine.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { filter, Subscription } from 'rxjs';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap/popover/popover';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { VariantService } from '@core/services/variant/variant.service';
import * as _ from 'lodash';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import {
	BlindPartsAvailability,
	BlindTypes,
	Material,
	MaterialType,
	PartStatusMap,
	SectionKeysEnum,
	ServerMaterial,
} from '@root/app.interfaces';
import { PartAvailabilityService } from '@core/services/part-availability/part-availability.service';
import { ColorPickerComponent } from '@shared/color-picker/color-picker.component';

declare const $: any;

@Component({
	selector: 'app-color-change',
	templateUrl: './color-change.component.html',
	styleUrls: ['./color-change.component.scss', './color-change.responsive.scss'],
})
export class ColorChangeComponent implements OnInit, OnDestroy, OnChanges {
	getBlindSelect: Subscription;
	availabilitySubs: Subscription;

	@Input() blindId: number;
	@Output() colorData = new EventEmitter();
	@ViewChildren('colorPicker') colorPickers!: QueryList<ColorPickerComponent>;

	protected readonly BlindTypes = BlindTypes;
	protected readonly MaterialType = MaterialType;
	protected readonly isShop = this.variant.isShop();
	protected sunscreenAvailability: PartStatusMap;

	blindSetupData: { material: Material } = { material: null };
	color = '';
	type = '';
	serverMaterialData: ServerMaterial[];
	blindType: string;
	sessionConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);
	sessionText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);
	isDesignType: boolean = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;
	materialData: ServerMaterial[];
	activeMaterialId: number | null = null;

	defaultColor = '#999999';

	dontSet = this.variant.isShop(); // Variable used to stop material data being displayed + stop material type button being active (until user makes a selection)

	constructor(
		private engineService: EngineService,
		private sessionStorageService: SessionStorageService,
		private shareDataService: ShareService,
		private saveSessionBlind: SaveSessionBlindsService,
		// private changeDetection: ChangeDetectorRef,
		private variant: VariantService,
		private blindStateService: BlindStateService,
		private partAvailabilityService: PartAvailabilityService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		const getCurrentBLindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);

		if (changes.blindId.currentValue === getCurrentBLindId) {
			setTimeout(this.setDefaultValue.bind(this));
		}
	}

	ngOnInit(): void {
		const blind = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		this.blindType = blind?.type;
		this.serverMaterialData = this.sessionConfig && this.sessionConfig[this.blindType].material;
		this.dontSet = blind?.setup?.material ? !blind.setup.material.configured : this.dontSet;
		this.getMaterialData();
		this.getStorageColors();
		this.setAvailabilitySubs();
		this.getBlindSelect = this.shareDataService.getBlindSelect
			.pipe(filter(({ id }) => id === this.blindId))
			.subscribe(this.setBlindSelectId.bind(this));
	}

	ngOnDestroy(): void {
		this.getBlindSelect.unsubscribe();
		this.availabilitySubs.unsubscribe();
	}

	getStorageColors(id?: number): void {
		const materialStorage = this.sessionStorageService.getBlindItemById(id || this.blindId, STORAGE_NAMES.zip_blind_data);

		if (materialStorage) {
			for (const item of materialStorage) {
				if (item.setup && item.setup.material) {
					this.color = item.setup.material.color;
					this.type = item.setup.material.type;
					this.activeMaterialId = item.setup.material.id;
					if (!this.dontSet) {
						this.activeMaterial.color.activeColor = this.color;
					}
				}
			}

			// When returning from overview page and not in shop, we want non-active materials to still show a default color
			if (!this.isShop && materialStorage[0].setup?.material) {
				for (const material of this.materialData) {
					if (material.id !== this.activeMaterialId) {
						material.color.activeColor = material.color.default;
					}
				}
			}
		}
	}

	setModelColor(hex): void {
		const opacity = this.activeMaterial?.opacity ?? this.materialData.find((material) => material.is_default)?.opacity;
		const texture = this.activeMaterial?.texture ?? this.materialData.find((material) => material.is_default)?.texture;
		const meshes = opacity >= MINIMUM_MATERIAL_OPACITY ? [...MATERIAL_MESH, ...SPLINE_MESH] : MATERIAL_MESH; // TODO: replace opacity check with more robust and clear solution

		for (const meshId of meshes) {
			this.engineService.setColor(meshId, hex, opacity, this.type, this.blindType, texture);
		}

		this.partAvailabilityService.updateBlind(this.blindId); // why is this here specifically?
	}

	onChangeColor(data: Material, saveChangeTime = true, buttonPressed = false): void {
		if (buttonPressed) {
			this.dontSet = false;
			this.activeMaterialId = data?.id;
		}

		this.color = data.color;
		this.type = data.type;

		this.setModelColor(data.color);

		const currentMaterial: ServerMaterial = this.materialData.find((material) => material.id === data?.id) ?? this.activeMaterial;

		if (this.activeMaterialId && !this.dontSet) {
			currentMaterial.color.activeColor = this.color;
		}

		if (this.isShop && buttonPressed) {
			this.onSelectMaterial(currentMaterial, false); // what is the point of this? // If this is true onSelectMaterial will return early anyway
		}

		const materialObject: Material = {
			id: currentMaterial.id,
			name: data.name,
			color: data.color,
			color_name: data.color_name,
			opacity: data.opacity,
			type: data.type,
			configured: !this.dontSet,
			is_extra_fee: currentMaterial.is_extra_fee,
		};

		this.blindSetupData = {
			material: materialObject,
		};

		this.saveBlindSetup(saveChangeTime);
		this.shareDataService.setMaterialColorChanged({ blind_id: this.blindId, ...materialObject });
	}

	onSelectMaterial(material: ServerMaterial, isColorPickerOpened: boolean): void {
		if (material.id === this.activeMaterialId && !this.dontSet) {
			return;
		}

		this.activeMaterialId = material.id;

		if (!material.color.can_be_changed) {
			this.activeMaterial.color.activeColor = material.color.default;
			this.color = material.color.default;
			this.type = material.type;
		} else if (this.activeMaterial.color.activeColor) {
			this.color = this.activeMaterial.color.activeColor;
			this.type = this.activeMaterial.type;
		}

		if (!isColorPickerOpened) {
			this.dontSet = !this.activeMaterial?.color.activeColor;

			const picker = this.colorPickers.find((picker) => picker.data?.id === material.id);
			picker?.open();
		}

		if (isColorPickerOpened && !this.activeMaterial.color.activeColor) {
			this.dontSet = true;
			this.blindStateService.markUnconfigured(this.blindId, SectionKeysEnum.Material);
		}

		const colorName = material.available_colors?.find((item) => item.color === this.color)?.name;
		const { id, name, opacity, type, is_extra_fee } = material;

		const materialObject: Material = {
			id,
			name,
			color: this.color,
			color_name: colorName,
			opacity,
			type,
			configured: !this.dontSet,
			is_extra_fee,
		};

		this.blindSetupData = {
			material: materialObject,
		};
		this.setModelColor(!_.isEmpty(this.color) ? this.color : this.defaultColor);
		this.saveBlindSetup();
		this.shareDataService.setMaterialColorChanged({ blind_id: this.blindId, ...materialObject });
	}

	// This function's sole use now is to overwrite the default color that the engine sets in setDefaultView() on blinds when returning from overview page, unless I am missing something else?
	// TODO: implement a better system for this as this function is now called a lot when it doesn't need to be.
	setBlindSelectId(): void {
		// this.blindType = this.sessionStorageService.getBlindItemById(data?.id, STORAGE_NAMES.zip_blind_data)[0].type;
		// this.serverMaterialData = this.sessionConfig[this.blindType].material;
		// this.getMaterialData();
		// this.getStorageColors(data.id);

		// if (data.id === this.blindId || data.status === 'new') {
		// 	if (this.activeMaterial && !this.dontSet) {
		// 		this.activeMaterial.color.activeColor = this.color;
		// 	}

		const setColorPalette = this.color ?? this.defaultColor;
		this.setModelColor(setColorPalette);

		// 	const colorProperty = {
		// 		color: setColorPalette,
		// 		type: this.type,
		// 		blind_id: this.blindId,
		// 	};

		// 	this.shareDataService.setMaterialColorChanged(colorProperty);
		// }

		// this.changeDetection.markForCheck();
	}

	setDefaultValue(): void {
		const materialConfig = this.sessionConfig[this.blindType].material; // could probably just use serverMaterialData?
		const materialStorage = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data);

		if (materialStorage?.[0].setup) {
			if (!this.isDesignType) {
				this.setBlindSelectId();
			}
			return;
		}

		for (const material of materialConfig) {
			if (material.is_default) {
				const colorArr = material.available_colors.find((color) => color.is_default);
				this.defaultColor = material.color.default;
				this.color = this.defaultColor;
				this.type = material.type;

				const colorProperty: Material = {
					id: material.id,
					name: material.name,
					color: this.defaultColor,
					color_name: colorArr.name,
					opacity: material.opacity,
					type: material.type,
				};

				this.activeMaterialId = material.id;
				if (!this.isShop) {
					this.activeMaterial.color.activeColor = this.color;
				}

				this.onChangeColor(colorProperty, false);
			} else if (!this.isShop) {
				material.color.activeColor = material.color.default;
			}
		}
	}

	saveBlindSetup(saveChangeTime = true): void {
		this.colorData.emit(this.blindSetupData);
		this.sessionStorageService.setBlindSetupDataById(this.blindId, this.blindSetupData, STORAGE_NAMES.zip_blind_data, saveChangeTime);

		this.saveSessionBlind.PutStorageDataToServer();
		this.shareDataService.setMaterialType({ blindId: this.blindId, type: this.blindSetupData.material.type as MaterialType });

		if (!this.dontSet) {
			this.blindStateService.markConfigured(this.blindId, SectionKeysEnum.Material);
		} else {
			this.blindStateService.markUnconfigured(this.blindId, SectionKeysEnum.Material);
		}
	}

	onPopover(popover: NgbPopover, button: HTMLElement): void {
		$(button).toggleClass('active', popover.isOpen());
	}

	getMaterialData(): void {
		this.materialData = this.serverMaterialData.map((item) => ({ ...item }));
	}

	setAvailabilitySubs(): void {
		this.availabilitySubs = this.shareDataService.getPartAvailability
			.pipe(filter(({ blindId }) => blindId === this.blindId))
			.subscribe(this.onAvailabilityChanges.bind(this));
	}

	onAvailabilityChanges(statuses: BlindPartsAvailability): void {
		if (statuses?.availability?.material) {
			const sunscreenMesh = this.serverMaterialData.filter((material) => material.type === MaterialType.Sunscreen)?.[0];
			this.sunscreenAvailability = statuses?.availability?.material.filter((item) => item.partId === sunscreenMesh?.id)?.[0];
		}
	}

	get activeMaterial() {
		return this.materialData.find((material) => material.id === this.activeMaterialId) ?? null;
	}
}
